import { useQuery } from '@tanstack/react-query'
import { UNIFIED_CHAINS_SUMMARY_KEY } from 'constants/tableQueryKeys'
import { useMemo } from 'react'
import useApiService from 'services/api'

/**
 * Restaurant (ChainsTable) data source — reads the CRM-reports read-model
 * (`reports/chains/` + `reports/chains/summary/`).
 *
 * ChainsTable is the one opps table that does NOT use `DECommonTable` — it has a
 * bespoke component (legacy + reports V2) with elastic search, a product
 * selector, and a rich summary header. So instead of the generic
 * `useUnifiedTableActions` hook, this returns the pieces the bespoke component
 * needs to read from the reports data source:
 *
 *  - `listAction`: a bound list fetcher with a
 *    `(params, signal) => Promise<{ results, count }>` shape. The `reports/chains/`
 *    endpoint already returns rows in the shape the chain columns, detail links,
 *    and row actions expect (nested under `chain`, with `contact_company_id`), so
 *    the rows are returned as-is.
 *  - `unifiedCount`: the reports summary count for the current filters, to drive
 *    pagination + `setTotalRowsInBackend` so paging matches the rows.
 *
 * (The `unified*` names on the return object are kept for a stable seam with the
 * consuming component; the data now comes from the reports read-model. Note:
 * the reports row already carries `chain_crm_report.sales_stages/account_owners`
 * natively — the unified adapter's `mirrorChainCrmReport` shim from #4268 is not
 * needed here, and #4268's CDC repair keeps that read-model data clean.)
 */

export function useUnifiedChainsTable(filterParams: Record<string, any>): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: any[]; count: number }>
  unifiedCount: number | undefined
  unifiedPlacesCount: number | undefined
  unifiedLtv: number | undefined
  unifiedSummaryFetching: boolean
} {
  const api = useApiService()

  const listAction = useMemo(() => {
    return async (params: Record<string, any>, signal?: AbortSignal) => {
      const res = await api.getChainsWithCRMReports(params, signal)
      return { results: res.results, count: res.count } as {
        results: any[]
        count: number
      }
    }
  }, [api])

  const { data: chainsSummary, isFetching: unifiedSummaryFetching } = useQuery({
    // Use the default (finite, 0) staleTime so the count refetches after
    // mutations that invalidate UNIFIED_CHAINS_SUMMARY_KEY. Do NOT set
    // `staleTime: Infinity` here: an infinite staleTime would leave the
    // count stale until a hard reload.
    queryKey: [UNIFIED_CHAINS_SUMMARY_KEY, filterParams],
    queryFn: async ({ signal }) =>
      api.getChainReportsSummary(filterParams, signal),
  })

  return {
    listAction,
    // The full stat-card set for the filtered chains, all from the reports
    // chains summary. `places_count` = summed open doors of the matching chains
    // ("Total Doors").
    unifiedCount: chainsSummary?.count,
    unifiedPlacesCount: chainsSummary?.places_count,
    unifiedLtv: chainsSummary?.total_ltv,
    unifiedSummaryFetching,
  }
}
